import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { DataResolver } from '@app/shared/resolver/data-ush-resolver/data.resolver';
import { environment } from '@env/environment';
import { IGuestsResponse, MsGuestsService } from '@upr-web-primer/ms-guests';
import { LocalStorageService } from '@upr-web-primer/storage';
import { UdxSpinnerService } from '@upr-web-primer/udx-spinner';
import { filter, map, orderBy } from '@upr-web-primer/udx-utils';
import { WINDOW } from '@upr-web-primer/window';
import { DateTime } from 'luxon';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  EMPTY,
  finalize,
  from,
  mergeMap,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { BaseViewEntitlementComponent } from '../view-entitlement/base-view-entitlement.component';
@Component({
  selector: 'app-view-ush-entitlement',
  templateUrl: './view-entitlement.component.html',
  styleUrls: ['./view-entitlement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ViewEntitlementComponent
  extends BaseViewEntitlementComponent
  implements OnInit, OnDestroy
{
  entitlementArray: any;
  isEmptyEntitlement = false;
  isShowEntitlement = false;
  pageLabels: any;
  userProfileData: any;
  userData: any;
  fullName: any;
  travelMembersDetails: any[];
  tabData: any = [];
  guestTicketsArray: any[];
  travelPartyTicketsArray: any[];
  travelMembersIds: any[];
  travelIdNames: any[];
  ticketDetailArray: any = [];
  selectedTabIndex: number;
  dateEntry: any;
  legacyDescription: any;
  showTabs = false;
  expiredTicketsArray: any[] = [];
  travelPartyId: any;
  eventTargeturl: any;
  private readonly destroy$ = new Subject<void>();
  displayParkNames = environment.config?.displayParkNames;
  parksRequestInProgress = new Set<string>();
  cfarEnabled = environment?.cfarEnabled;
  selfServiceEnabled = environment?.selfServiceEnabled;
  deleteAbNormalCard: boolean = false;
  cartId: string = '';
  constructor(
    protected override injector: Injector,
    commonUtilityService: CommonUtilityService,
    guestsService: MsGuestsService,
    cdRef: ChangeDetectorRef,
    title: Title,
    localStorage: LocalStorageService,
    @Inject('AppRoutes') public override appRoutes: any,
    @Inject('GlobalDefaults') public override globalDefault: any,
    @Inject('AppDefaults') public override appDefaults: any,
    @Inject(WINDOW) window: any,
    cookieService: CookieService,
    private readonly router: ActivatedRoute,
    private readonly dataResolver: DataResolver,
    private readonly spinner: UdxSpinnerService,
  ) {
    super(
      injector,
      commonUtilityService,
      guestsService,
      cdRef,
      title,
      localStorage,
      appRoutes,
      globalDefault,
      appDefaults,
      window,
      cookieService,
    );
  }

  // ─── Abstract hook implementations ────────────────────────────────────

  protected getTicketIdFromData(data: any): string {
    return data.id;
  }

  protected getTicketGuestId(item: any): string {
    return item.guest_details?.assigned_guest_id;
  }

  protected getDateSortField(): string {
    return 'validity.valid_from_datetime';
  }

  protected getDateSortValue(item: any): string {
    return item.validity.valid_from_datetime;
  }

  /**
   * ngOnInit-It will load the component
   */
  ngOnInit(): void {
    this.initWalletData();
    this.pageLabels = this.commonUtilityService.getResolvedPageData();

    const updateLogicEntitlement = (): void => {
      this.entitlementArray = this.commonUtilityService.getWalletResolvedData();
      this.entitlementArray = this.entitlementArray.map((entitlement: any) => {
        if (entitlement?.tridionContent) {
          entitlement['tridionContentProductName'] =
            this.commonUtilityService.getProductNameFromTridion(
              entitlement?.tridionContent,
              entitlement?.plu,
            );
        }
        return entitlement;
      });
      this.entitlementArray = filter(this.entitlementArray, (item: any) => {
        if (item?.status === this.appDefaults.ACTIVE_ENTITLEMENT_STATUS) {
          if (
            item?.validity.hasOwnProperty('valid_through_datetime') &&
            this.isGreaterDateValid(item.validity.valid_through_datetime)
          ) {
            return true;
          } else if (!item?.validity.hasOwnProperty('valid_through_datetime')) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      });
      this.initCommonEntitlementSetup();
      this.getEntitlement();
    };

    this.initEntitlementLoading(this.dataResolver, updateLogicEntitlement);

    if (sessionStorage.getItem('entitlementLoaded')) {
      updateLogicEntitlement(); // Run it immediately
    }

    if (this.cfarEnabled || this.selfServiceEnabled) {
      this.commonUtilityService
        .checkForAbnormalCart()
        .pipe(takeUntil(this.destroy$))
        .subscribe((cartInfo) => {
          this.deleteAbNormalCard = cartInfo.shouldDelete;
          this.cartId = cartInfo.cartId;
        });
    }
  }

  /**
   * getDate - It will format the date based on locale
   */
  getDate(date: any): string {
    return this.commonUtilityService.convertToParkTime(date);
  }

  /**
   * getToDate - It will format the date based on locale
   */
  getToDate(date: any): string {
    return date ? `-${this.commonUtilityService.convertToParkTime(date)}` : '';
  }

  /**
   * getEntitlement-It will load the entitlement data
   */
  getEntitlement(): void {
    let routerFragment;
    this.router.fragment.subscribe((data: any) => {
      routerFragment = data;
    });
    const guestId = routerFragment || this.guestsService.helper.getGuestId();
    this.loadUserInfo();
    const entitledArray = this.commonUtilityService.getResolvedData();
    this.expiredTicketsArray =
      filter(
        entitledArray,
        (item: any) =>
          item?.status?.toLowerCase() ==
            (this.globalDefault.EXPIRED_ORDER.toLowerCase() ||
              this.globalDefault.CANCELLED.toLowerCase() ||
              this.globalDefault.INACTIVE_ORDER.toLowerCase()) &&
          this.isLessDateValid(item?.validity?.valid_through_datetime),
      ) || [];
    this.initEmptyEntitlementCheck();
    this.travelPartyId = guestId;
    this.guestTicketsArray = filter(this.entitlementArray, (item: any) => {
      return (
        item.guest_details?.assigned_guest_id === guestId &&
        item?.status === this.appDefaults.ACTIVE_ENTITLEMENT_STATUS
      );
    });
    this.updateEmptyEntitlementState();
    this.filterTravelPartyTickets(guestId);
    this.getTravelParty();
    this.sortByDate();
    this.sortEntitlements();
    this.getProductDetails();
    this.cdRef.markForCheck();
  }

  getProductDetails(): void {
    const tickets = this.guestTicketsArray;
    if (!Array.isArray(tickets) || !tickets.length) {
      return;
    }
    const ticketIndexesByPlu = new Map<string, number[]>();
    tickets.forEach((ticket, index) => {
      const plu = ticket?.plu;
      if (!plu) {
        return;
      }
      const indexes = ticketIndexesByPlu.get(plu) ?? [];
      indexes.push(index);
      ticketIndexesByPlu.set(plu, indexes);
    });
    const productIds = [...ticketIndexesByPlu.keys()].filter((plu) => {
      const indexes = ticketIndexesByPlu.get(plu) ?? [];
      const parksAlreadyLoaded = indexes.every((index) => {
        const parks = this.guestTicketsArray[index]?.parks;
        return parks !== undefined && parks !== null;
      });

      const requestInProgress = this.parksRequestInProgress.has(plu);
      return !parksAlreadyLoaded && !requestInProgress;
    });
    if (!productIds?.length) {
      return;
    }
    productIds.forEach((plu) => {
      this.parksRequestInProgress.add(plu);
    });
    this.spinner.show();
    from(productIds)
      .pipe(
        mergeMap(
          (plu) =>
            this.commonUtilityService.getBaseProduct(plu).pipe(
              tap((product: any) => {
                if (!product) {
                  return;
                }
                if (
                  product.parks !== undefined &&
                  typeof product.parks !== 'string'
                ) {
                  console.warn(
                    'Unexpected parks type from API:',
                    typeof product.parks,
                  );
                  return;
                }
                const indexes = ticketIndexesByPlu.get(plu) ?? [];
                if (!indexes?.length) {
                  return;
                }
                const updatedTickets = [...this.guestTicketsArray];
                indexes.forEach((index) => {
                  const ticket = updatedTickets[index];

                  if (ticket?.plu !== plu) {
                    return;
                  }
                  updatedTickets[index] = {
                    ...ticket,
                    productData: product,
                  };
                });
                this.guestTicketsArray = updatedTickets;
                this.cdRef.markForCheck();
              }),
              catchError((error) => {
                this.spinner.hide();
                console.error(
                  `Failed to fetch Product details for PLU: ${plu}`,
                  error,
                );
                return EMPTY;
              }),
              finalize(() => {
                this.parksRequestInProgress.delete(plu);
              }),
            ),
          5,
        ),
        takeUntil(this.destroy$),
        finalize(() => {
          this.spinner.hide();
          this.cdRef.markForCheck();
        }),
      )
      .subscribe();
  }

  getDescription(ticket: any): string {
    const date =
      ticket?.source_system_details?.total_days > 1
        ? this.getDate(ticket.validity?.valid_from_datetime) +
          this.getToDate(ticket.validity?.valid_through_datetime)
        : this.getDate(ticket.validity?.valid_from_datetime);
    if (ticket?.productData?.parks && this.displayParkNames) {
      const template = this.pageLabels?.['product.parkNames.Txt'];
      if (!template) {
        return date;
      }
      return template
        ?.split('###parks###')
        .join(ticket?.productData?.parks)
        ?.split('###date###')
        .join(date);
    }
    return date;
  }

  isGreaterDateValid(validDate: string): boolean {
    const dateTime = DateTime.fromISO(validDate, { setZone: true });
    const todaysDate = DateTime.now();
    return dateTime > todaysDate;
  }

  override isLessDateValid(validDate: string): boolean {
    const dateTime = DateTime.fromISO(validDate, { setZone: true });
    const todaysDate = DateTime.now();
    return dateTime < todaysDate;
  }

  /**
   * getTravelParty-It will load the TRP data
   */
  getTravelParty(): void {
    const { equal, notEqual } = this.partitionTravelMembers();
    this.travelMembersIds = [...equal, ...notEqual];
    const namesWithIds = map(this.travelMembersIds, (item: any) => ({
      id: item?.member_id,
      full_name: `${item?.contact?.first} ${item?.contact?.last}`,
    }));
    this.buildTravelPartyTabs(namesWithIds);
    this.tabData = Array.from(
      new Set(this.tabData.map((a: { permaLink: any }) => a.permaLink)),
    ).map((id) => {
      return this.tabData.find((a: { permaLink: any }) => a.permaLink === id);
    });
    this.showTabs = true;
    this.cdRef.markForCheck();
    this.ticketDetailArray = this.entitlementArray;
  }

  /**
   * loadUserInfo-It will load the user data
   */
  loadUserInfo(): any {
    this.guestsService.profile
      .getByGuestId(this.guestsService.helper.getGuestId())
      .then((userData: IGuestsResponse) => {
        this.userProfileData = userData?.personal_info?.contacts;
      })
      .catch((errors: any) => {
        if (errors.status == this.globalDefault.ERROR_401) {
          this.commonUtilityService.setUser();
          localStorage.clear();
          sessionStorage.clear();
          this.cookieService.deleteAll('/', environment.cookieSubDomain);
          const redirectUrl = this.commonUtilityService.getOidcRedirectionUrl();
          this.commonUtilityService.redirectToOidcAppEndSession(redirectUrl);
        }
      });
  }

  /**
   * goToPage-It will navigate to dashboard component
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  goToPage(type: any) {
    switch (type) {
      case this.pageLabels?.['page.dashboard']:
        this.commonUtilityService.changeRoute(this.appRoutes.DASHBOARD);
        this.eventTargeturl = this.commonUtilityService.getTargetUrl(
          this.appRoutes.DASHBOARD,
        );
        this.ddlEvents(
          this.pageLabels?.['page.dashboard'],
          this.eventTargeturl,
        );
        break;
    }
  }

  /**
   * DDL Events
   */
  ddlEvents(label: any, url: any): void {
    this.commonUtilityService.invokeFireEvent(
      label,
      'Button',
      'Click',
      '',
      new Date().toISOString(),
      'Body | CTA: ' + label,
      url,
      this.window?.digitalData?.page?.category?.primaryCategory ?? '',
      this.window?.digitalData?.page?.category?.subCategory1 ?? '',
    );
    this.finalizeTravelParty();
  }

  /**
   * updateTab-It will update the tab based on tab selection
   */
  updateTab(event: CustomEvent, _tabData: any): void {
    this.selectedTabIndex = event.detail;
    if (this.selectedTabIndex > 0) {
      const index = this.tabData[this.selectedTabIndex];
      this.travelPartyId = index?.permaLink;
      this.fullName = index.heading;
      this.guestTicketsArray = filter(
        this.entitlementArray,
        (item: any) =>
          item.guest_details?.assigned_guest_id === index.permaLink &&
          item?.status === this.appDefaults.ACTIVE_ENTITLEMENT_STATUS,
      );
    } else {
      const index = this.tabData[this.selectedTabIndex];
      this.fullName = index.heading;
      this.guestTicketsArray = filter(
        this.entitlementArray,
        (item: any) =>
          item.guest_details?.assigned_guest_id === index.permaLink &&
          item?.status === this.appDefaults.ACTIVE_ENTITLEMENT_STATUS,
      );
    }
    this.sortByDate();
    if (this.guestTicketsArray?.length <= 0) {
      this.isEmptyEntitlement = true;
      this.isShowEntitlement = false;
    } else {
      this.isEmptyEntitlement = false;
      this.isShowEntitlement = true;
    }
    this.getProductDetails();
    this.sortEntitlements();
  }

  /**
   * getTickets-It will navigate to ticket-detail component
   */
  getTickets(data: any): any {
    sessionStorage.removeItem('pastTicketsPage');
    this.commonUtilityService.changeRoute(
      `${this.appRoutes.TICKET_DETAILS}/${data.id}`,
    );
    this.eventTargeturl = this.commonUtilityService.getTargetUrl(
      `${this.appRoutes.TICKET_DETAILS}/${data.id}`,
    );
    this.ddlEvents(this.pageLabels?.['page.ticketDetail'], this.eventTargeturl);
  }

  /**
   * oldSystemLink-It will redirect to legacy Url
   */
  legacyLink(): void {
    window.open(
      this.pageLabels?.['linkticket.legacyOrderLink'],
      this.appDefaults.BLANK,
    );
  }

  /**
   * sortByDate - It will sort the content in ascending order as per valid_from
   */
  sortByDate(): void {
    this.guestTicketsArray = orderBy(
      this.guestTicketsArray,
      ['validity.valid_from_datetime'],
      ['asc'],
    );
  }

  /**
   * sortEntitlements - sort the entitlements
   */
  sortEntitlements(): void {
    this.guestTicketsArray.sort((a: any, b: any) => {
      // Compare by walletEntitlementSorting order
      const orderA = a.walletEntitlementSorting;
      const orderB = b.walletEntitlementSorting;
      if (orderA < orderB) {
        return -1;
      }
      if (orderA > orderB) {
        return 1;
      }

      return (
        new Date(a.validity.valid_from_datetime).getTime() -
        new Date(b.validity.valid_from_datetime).getTime()
      );
    });
  }

  /**
   * navigateToStore - Clears cookies and navigates to the web ticket store
   */
  navigateToStore(): any {
    this.commonUtilityService.clearAllCookies(false, false, false, false);
    this.commonUtilityService.deleteSelfServiceCookies();
    const targeturl = this.commonUtilityService.onClickRedirectWTS();
    this.ddlEvents(this.pageLabels?.['viewEntEmpty.buyTicketsCTA'], targeturl);
  }

  /**
   * redirectToWTS - Navigate back to WTS account
   */
  redirectToWTS(): void {
    this.commonUtilityService.deleteAbnormalCartAndExecute(
      this.deleteAbNormalCard,
      this.cartId,
      () => this.navigateToStore(),
    );
  }

  /**
   * redirectToWTS - Navigate back to WTS account
   */
  redirectTolinkticket(): any {
    this.commonUtilityService.setLinkRoute(this.appRoutes.VIEW_ENTITLMENT);
    this.commonUtilityService.changeRoute(this.appRoutes.LINK_TICKETS);
    this.eventTargeturl = this.commonUtilityService.getTargetUrl(
      this.appRoutes.LINK_TICKETS,
    );
    this.ddlEvents(
      this.pageLabels?.['viewEntEmpty.linkTicketsCTA'],
      this.eventTargeturl,
    );
  }

  /**
   * viewPastTickets - Navigate back to past tiickets
   */
  viewPastTickets(): void {
    this.commonUtilityService.changeRoute(this.appRoutes.PAST_TICKETS);
    this.eventTargeturl = this.commonUtilityService.getTargetUrl(
      this.appRoutes.PAST_TICKET,
    );
    this.ddlEvents(
      this.pageLabels?.['viewEntEmpty.seePastTickets'],
      this.eventTargeturl,
    );
    sessionStorage.setItem('wallet', 'true');
    this.commonUpdateTabActions();
  }

  getAriaLabelDate(data: any): string {
    const heading =
      data?.tridionContentProductName ||
      data?.commerceProductName ||
      data?.name ||
      '';

    const fromDate = this.formatAriaLabelDate(
      data?.validity?.valid_from_datetime,
    );

    const toDate = this.formatAriaLabelDate(
      data?.validity?.valid_through_datetime,
    );

    const description =
      data?.source_system_details?.total_days > 1
        ? `${fromDate} to ${toDate}`
        : fromDate;

    return `${heading}. ${description}`;
  }

  formatAriaLabelDate(date: string): string {
    if (!date) {
      return '';
    }

    const parkTimeDate = this.commonUtilityService.convertToParkTime(date);

    return DateTime.fromJSDate(new Date(parkTimeDate)).toLocaleString(
      DateTime.DATE_FULL,
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
